/* eslint-disable consistent-return */
/* eslint-disable no-shadow */
/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import MasterTable from './Tables/MasterTable';
import { Modal, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';
import ChangeStatus from './Modal/ChangeStatus';
import ReviewQColumns from './Tables/ReviewQColumns';
import ReviewQRow from './Tables/ReviewQRow';
import QueueOutlinedIcon from '@mui/icons-material/QueueOutlined';
import RollUpStats from './Cards/RollUpStats';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  useGetEntitiesByGroupId,
} from 'api/bitedb/entities/entities.hooks';
import {
  EntitySelectionAction,
  useEntitySelectionActions,
} from './common/useEntitySelectionActions';
import { PaginationParams } from 'api/bitedb/entities/entities';
import { useGetStatsByGroupId } from 'api/bitedb/stats/stats.hooks';

// Helper Hook for Debouncing
const useDebounce = (value: string, delay: number) => {
  const [ debouncedValue, setDebouncedValue ] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [ value, delay ]);
  return debouncedValue;
};

interface Entity {
  id: string;
  name: string;
  description: string;
  category: string;
  entity_id?: string;
  subcategory?: string;
  email?: string;
  phone?: string;
  address?: string;
  database_id?: string;
  database_name?: string;
  country_of_origin?: string;
  risk_level: string;
  status_flag: string;
  created_at?: string;
  updated_at?: string;
  linked_products_count?: number;
}

const ReviewQueue: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const location = useLocation();

  // URL state management
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [ location.search ],
  );

  const page = parseInt(queryParams.get('page') || '1', 10);
  const limit = parseInt(queryParams.get('limit') || '10', 10);
  const sortBy = queryParams.get('sortBy') || 'hit_count';
  const sortOrder = (queryParams.get('sortOrder') as 'asc' | 'desc') || 'desc';
  const urlSearchTerm = queryParams.get('search') || '';

  // Local state
  const [ selectedItems, setSelectedItems ] = useState<any[]>([]);
  const [ isDeletingItems, setIsDeletingItems ] = useState(false);
  const [ isChangingStatus, setIsChangingStatus ] = useState(false);
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState(false);
  const [ localSearchTerm, setLocalSearchTerm ] = useState(urlSearchTerm);
  const [ screenThirtyDays, setScreenThirtyDays ] = useState<number>(0);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);

  const handleUrlChange = useCallback(
    (newParams: Record<string, string | number>) => {
      const updatedParams = new URLSearchParams(location.search);
      Object.entries(newParams).forEach(([ key, value ]) => {
        if (value) {
          updatedParams.set(key, String(value));
        } else {
          updatedParams.delete(key);
        }
      });
      history.replace({ search: updatedParams.toString() });
    },
    [ history, location.search ],
  );

  useEffect(() => {
    if (debouncedSearchTerm !== urlSearchTerm) {
      handleUrlChange({ search: debouncedSearchTerm, page: 1 });
    }
  }, [ debouncedSearchTerm, urlSearchTerm, handleUrlChange ]);

  const paginationParams: PaginationParams = {
    page: page,
    limit: limit,
    sortBy: sortBy,
    sortOrder: sortOrder,
    exclusions: [ 'cleared', 'quarantined', 'barred' ],
  };

  // React Query hooks for entities with server-side pagination
  const {
    data: entitiesData,
    pagination: entitiesPagination,
    isLoading: isEntitiesLoading,
    error: entitiesError,
    refetch: refetchEntities,
  } = useGetEntitiesByGroupId(
    cur_user?.selected_team?.id ?? '-1',
    debouncedSearchTerm,
    paginationParams,
  );

  // Mutation hooks
  const {
    removeDatabaseEntitiesMutation,
    updateDatabaseEntitiesStatusMutation,
    runSelectionAction,
  } = useEntitySelectionActions(cur_user?.selected_team?.id);

  // Handle entities loading errors
  useEffect(() => {
    if (entitiesError) {
      enqueueSnackbar('Failed to fetch entities', { variant: 'error' });
    }
  }, [ entitiesError ]);

  useEffect(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    const recentEntities =
      entitiesData?.filter((entity) => {
        if (!entity.created_at) {
          return false;
        }
        const createdDate = new Date(entity.created_at);
        return createdDate >= thirtyDaysAgo && createdDate <= now;
      }) || [];
    setScreenThirtyDays(recentEntities.length);
  }, [ entitiesData ]);

  const handleSelectAll = () => {
    setSelectedItems(
      selectedItems.length === entitiesData?.length ? [] : entitiesData || [],
    );
  };

  const handleSort = (key: string) => {
    const newSortOrder = sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
    handleUrlChange({ sortBy: key, sortOrder: newSortOrder });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    handleUrlChange({ page: newPage });
  };

  const handleBulkAction = async(
    action: EntitySelectionAction,
    status?: string,
  ) => {
    await runSelectionAction(selectedItems, action, status, () => {
      setSelectedItems([]);
      setIsDeletingItems(false);
      setIsChangingStatus(false);
    });
  };

  const handleDeleteEntity = async() => {
    await handleBulkAction('delete');
  };

  const handleUpdateEntity = async(status: string) => {
    await handleBulkAction('updateStatus', status);
  };

  const handleHitCountUpdate = (id: string, count: number) => {
    // Note: With React Query, hit count updates should trigger a refetch
    // or be handled through the mutation hooks for real-time updates
    refetchEntities();
  };

  const handleEdit = async(item: any) => {
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];
      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);
      return alreadySelected ?
        safePrevSelected.filter((i) => i.id !== item.id) :
        [ ...safePrevSelected, item ];
    });
    setIsChangingStatus(true);
  };

  const handleDelete = async(item: any) => {
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];
      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);
      return alreadySelected ?
        safePrevSelected.filter((i) => i.id !== item.id) :
        [ ...safePrevSelected, item ];
    });
    setIsDeletingItems(true);
  };

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useGetStatsByGroupId(cur_user?.selected_team?.id ?? '-1');

  return (
    <div className='team-content-container'>
      <div className='people-col'>
        <div className='team-content-header' style={{ gap: '10px' }}>
          <div className='team-content-search'>
            <div className='card-db-header'>
              <div className='card-db-icon'>
                <QueueOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
                />
              </div>
              <div className='card-db-title'>{'Review Queue'}</div>
            </div>
          </div>
        </div>
        <div className='team-content-header'>
          <div className='team-content-search'>
            <span className='card-db-meta-span'>{'Manage your queue '}</span>
          </div>
        </div>
      </div>
      <>
        <div
          className='api-container-info'
          style={{ padding: '0px 0px 0px 0px', width: '100%' }}
        >
          <div
            className='card-grid-six'
            style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
          >
            <RollUpStats
              svgIcon={
                <ReportProblemOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
                />
              }
              title={'Review Queue'}
              total={`${
                (stats?.total_not_reviewed_entities || 0) +
                (stats?.total_new_entities || 0)
              } `}
            />
            <Link
              to={{
                pathname: '/stats-roll-up',
                state: { status_flag: 'cleared' },
              }}
            >
              <RollUpStats
                svgIcon={
                  <CalendarTodayOutlinedIcon
                    sx={{ fontSize: 20, color: 'var(--Primary-400, #16a34a)' }}
                  />
                }
                title={'Cleared'}
                total={`${stats?.total_cleared_entities ?? '0'}`}
                // total={'0'}
              />
            </Link>
            <Link
              to={{
                pathname: '/stats-roll-up',
                state: { status_flag: 'quarantined' },
              }}
            >
              <RollUpStats
                svgIcon={
                  <WorkOutlineOutlinedIcon
                    sx={{ fontSize: 20, color: 'var(--Primary-400, #f59e0b)' }}
                  />
                }
                title={'Quarantine'}
                total={`${stats?.total_quarantined_entities ?? '0'}`}
              />
            </Link>
            <Link
              to={{
                pathname: '/stats-roll-up',
                state: { status_flag: 'barred' },
              }}
            >
              <RollUpStats
                svgIcon={
                  <NotInterestedOutlinedIcon
                    sx={{ fontSize: 20, color: 'var(--Primary-400, #dc2626)' }}
                  />
                }
                title={'Barred'}
                // total={'0'}
                total={`${stats?.total_barred_entities ?? '0'}`}
                // total={`${originalStats.barred}`}
              />
            </Link>
          </div>
        </div>

        <div className='team-content-header'>
          <div className='team-content-search'>
            <div className='screening-input-field-container'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
              >
                <path
                  d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z'
                  stroke='var(--card-text)'
                  strokeWidth='1.66667'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <input
                placeholder={'Search Queue'}
                id='primary'
                type='text'
                className='screening-input-field-content'
                name='primary'
                value={localSearchTerm}
                onChange={(e) => {
                  setLocalSearchTerm(e.target.value);
                }}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    return;
                  }
                }}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '40%',
              gap: '10px',
              justifyContent: 'flex-end',
            }}
          >
            <Tooltip
              title={'Update Status'}
              placement='bottom-start'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, var(--bg-colors))',
                  'color': 'var(--text-prim25)',
                    'maxWidth': '700px',
                    '& .MuiTooltip-arrow': {
                    color: 'var(--Background-200, var(--bg-colors))',
                    },
                  },
                },
              }}
            >
              <div
                className='audit-btn-star'
                onClick={() => setIsChangingStatus(true)}
              >
                <EditOutlinedIcon sx={{ fontSize: 20, color: 'var(--card-text)' }} />
              </div>
            </Tooltip>
            <Tooltip
              title={'Remove from Database'}
              placement='bottom-start'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    'display': 'flex',
                    'padding': 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    'flexDirection': 'column',
                    'alignItems': 'flex-start',
                    'alignSelf': 'stretch',
                    'borderRadius': 'var(--radius-md, 8px)',
                    'background': 'var(--Background-200, var(--bg-colors))',
                    'color': 'var(--text-prim25)',
                    'maxWidth': '700px',
                    '& .MuiTooltip-arrow': {
                    color: 'var(--Background-200, var(--bg-colors))',
                    },
                  },
                },
              }}
            >
              <div
                className='audit-btn-star'
                onClick={() => setIsDeletingItems(true)}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
                    stroke='var(--card-text)'
                    strokeWidth='1.66667'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </Tooltip>
          </div>
        </div>

        <MasterTable
          cols={
            <ReviewQColumns
              handleCheckboxClick={handleSelectAll}
              isSelectedItems={
                selectedItems.length === entitiesData?.length &&
                entitiesData?.length > 0
              }
              sortData={handleSort}
              sortConfig={{ key: sortBy, direction: sortOrder }}
            />
          }
          loading={isEntitiesLoading}
          location={'/supplier'}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          suppliersData={entitiesData}
          currentPage={page}
          totalPages={entitiesPagination?.totalPages || 1}
          onPageChange={handlePageChange}
          customRowComponent={(props: any) => (
            <ReviewQRow
              location={`/supplier/${props.supplier.entity_id}`}
              viewType='Review Queue'
              supplier={props.supplier}
              selectedItems={props.selectedItems}
              setSelectedItems={props.setSelectedItems}
              onHitCountUpdate={handleHitCountUpdate}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
            />
          )}
        />
      </>

      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <DeleteCommonModal
          handleClose={() => setIsDeleteModalOpen(false)}
          handleDelete={() => setIsDeleteModalOpen(false)}
          title={'Delete Database?'}
          paragraph={
            'Once Deleted there is no way to recover this Database or the infomation saved on it.'
          }
        />
      </Modal>
      <Modal open={isDeletingItems} onClose={() => setIsDeletingItems(false)}>
        <DeleteCommonModal
          handleClose={() => setIsDeletingItems(false)}
          handleDelete={handleDeleteEntity}
          title={`Remove ${selectedItems.length} ${
            selectedItems.length === 1 ? 'entity' : 'entities'
          } from their Database?`}
          paragraph={
            "Each entity is removed only from the Database it is listed in here, together with its Screening History there. It stays in your team's other Databases."
          }
          confirmLabel='Remove'
          loadingLabel='Removing'
          isLoading={removeDatabaseEntitiesMutation.isPending}
        />
      </Modal>
      <Modal open={isChangingStatus} onClose={() => setIsChangingStatus(false)}>
        <ChangeStatus
          isChangingStatus={updateDatabaseEntitiesStatusMutation.isPending}
          handleClose={() => setIsChangingStatus(false)}
          curStatus={'new'}
          changeStatus={handleUpdateEntity}
        />
      </Modal>
    </div>
  );
};

export default ReviewQueue;
